import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Typography from "@mui/material/Typography";
import { startLogin } from "../../../actions/auth";
import {
  AUTH_PAGE_BRANDING_LOGO_ALT,
  EMAIL_PLACEHOLDER,
  FIELD_EMAIL_VALID,
  FIELD_REQUIRED,
  LOG_IN,
  PASSWORD_PLACEHOLDER,
} from "../../../i18n/keysTranslations";
import { useForm } from "react-hook-form";
import useFocusErrorForm from "../../../hooks/useFocusErrorForm";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import { REGEX_EMAIL } from "../../../utils/regex";
import TextField from "../../../components/Form/TextField";
import { useTranslationApp } from "../../../lib/i18next";
import Button from "../../../components/Buttons/Button";
import { BOX_SHADOW_FIELDS, INPUT_COLOR_BORDER } from "../../../utils/colors";
import { BORDER_RADIUS } from "../../../utils/constants";

const LoginFormOwner = () => {
  const { control, handleSubmit, formState } = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
    mode: "onChange",
  });

  const errors = formState.errors;
  useFocusErrorForm(formState);
  const [isFetching, setIsFetching] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);

  const { theme } = useSelectorApp((state) => state.ui);
  const dispatch = useDispatchApp();
  const navigate = useNavigate();

  const { t } = useTranslationApp();

  const onChangeShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const onSubmit = async (formValues) => {
    setIsFetching(true);
    const response = await dispatch(
      startLogin(formValues.email, formValues.password)
    );
    setIsFetching(false);
    if (!response.ok) {
      setError(response.message);
      return;
    }

    navigate("/dashboard");
  };

  return (
    <Box
      component="div"
      sx={{
        width: "100%",
        minWidth: { xs: "auto", sm: 400 },
        maxWidth: 400,
        mx: "auto",
      }}
    >
      <Box sx={{ textAlign: "start" }}>
        <Avatar
          src={theme.logoLogin}
          alt={t(AUTH_PAGE_BRANDING_LOGO_ALT)}
          variant="square"
          sx={{
            width: "100px",
            height: "auto",
            maxHeight: "60px",
            ".MuiAvatar-img": {
              maxWidth: "160px",
              objectFit: "contain",
            },
          }}
        />
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          mt: 2,
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              backgroundColor: "fields.background",
              color: "background.paperContrastText",
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              borderRadius: BORDER_RADIUS,
              boxShadow: BOX_SHADOW_FIELDS,
            }}
          >
            {error}
          </Alert>
        )}

        <Typography variant="h6" color="text.primary" sx={{ fontSize: 14 }}>
          {t(EMAIL_PLACEHOLDER)}
        </Typography>

        <TextField
          margin="dense"
          fullWidth
          id="login-owner-email"
          name="email"
          placeholder="m@example.com"
          autoComplete="email"
          autoFocus
          variant="filled"
          color="primary"
          sx={{
            borderRadius: 3,
            mb: 2,
            "& .MuiInputBase-input": {
              minHeight: "10px !important",
            },
          }}
          InputLabelProps={{ shrink: true }}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
            validate: {
              emailValid: (value) => {
                if (!REGEX_EMAIL.test(value)) {
                  return t(FIELD_EMAIL_VALID);
                }
              },
            },
          }}
          errors={errors}
        />
        <Typography variant="h6" color="text.primary" sx={{ fontSize: 14 }}>
          {t(PASSWORD_PLACEHOLDER)}
        </Typography>
        <TextField
          margin="dense"
          fullWidth
          name="password"
          type={showPassword ? "text" : "password"}
          id="login-owner-password"
          autoComplete="current-password"
          variant="filled"
          color="primary"
          sx={{
            borderRadius: 3,
            "& .MuiInputBase-input": {
              minHeight: "10px !important",
            },
          }}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <IconButton onClick={onChangeShowPassword}>
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
          }}
          errors={errors}
        />
        <Button
          loading={isFetching}
          type="submit"
          color="primary"
          fullWidth
          variant="contained"
          sx={{
            mt: 2,
            mb: 2,
          }}
        >
          {t(LOG_IN)}
        </Button>
      </Box>
    </Box>
  );
};

export default LoginFormOwner;
